import { useEffect } from 'react';
import { ApiError } from '@discovr/common';
import {
  useCampaignReport,
  useCampaignSeries,
  useCampaignHealth,
} from '@discovr/features/appsflyer';
import type { CampaignReport, CreatorReport, CampaignHealth, SeriesPoint } from '@discovr/types';
import { DownloadSimple, Info, Pulse, Receipt, Target, TrendUp } from '@phosphor-icons/react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { KpiStatCard } from '../components/dashboard/KpiStatCard';
import { DataTable } from '../components/common/DataTable';
import type { ColumnDef } from '../components/common/DataTable';
import { CHART_COLORS } from '../lib/chartColors';
import {
  BENCHMARK_ROAS,
  fmtAxisMoney,
  fmtInt,
  fmtMoney,
  fmtPct,
  fmtRoas,
  rate,
  roasBenchmarkSubtext,
} from './conversionsFormat';

interface ConversionsTabProps {
  /** Campaign id - RQ key + report/series/health fetch arg. */
  campaignId?: string;
  /** Reports whether AppsFlyer is configured for this campaign, once known
   *  (undefined while loading). Lets a parent stacking multiple attribution
   *  sources decide whether it needs its own shared "connect a tool" empty
   *  state, instead of this component always rendering its own. */
  onConnectionChange?: (connected: boolean) => void;
}


function fmtDateShort(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtRelative(iso: string | null): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'never';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

type CreatorRow = CreatorReport & { id: string };

function creatorStatus(roas: number | null): { label: string; cls: string } {
  if (roas == null) return { label: 'No data', cls: 'bg-muted text-muted-foreground' };
  if (roas >= BENCHMARK_ROAS) return { label: 'Performing', cls: 'bg-emerald-50 text-emerald-700' };
  return { label: 'Below benchmark', cls: 'bg-amber-50 text-amber-700' };
}

const creatorColumns: ColumnDef<CreatorRow>[] = [
  {
    id: 'creator',
    header: 'Creator',
    cell: (r) => (
      <span className="font-mono text-caption-regular text-muted-foreground">
        {r.creator_id.slice(-8)}
      </span>
    ),
    minWidth: 200,
  },
  { id: 'clicks', header: 'Clicks', align: 'right', cell: (r) => fmtInt(r.clicks), minWidth: 80 },
  { id: 'installs', header: 'Installs', align: 'right', cell: (r) => fmtInt(r.installs), minWidth: 80 },
  { id: 'conversions', header: 'Conversions', align: 'right', cell: (r) => fmtInt(r.conversions), minWidth: 100 },
  { id: 'revenue', header: 'Revenue', align: 'right', cell: (r) => fmtMoney(r.revenue), minWidth: 100 },
  { id: 'fee', header: 'Spend', align: 'right', cell: (r) => fmtMoney(r.fee), minWidth: 80 },
  {
    id: 'roas',
    header: 'ROAS',
    align: 'right',
    cell: (r) => <span className="font-semibold tabular-nums">{fmtRoas(r.roas)}</span>,
    minWidth: 80,
  },
  {
    id: 'status',
    header: 'Status',
    cell: (r) => {
      const s = creatorStatus(r.roas);
      return (
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>
          {s.label}
        </span>
      );
    },
    minWidth: 120,
  },
];

function FunnelStage({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`flex flex-1 flex-col rounded-xl border px-4 py-3 ${
        accent ? 'border-emerald-200 bg-emerald-50/60' : 'border-border bg-card'
      }`}
    >
      <span className="text-[10px] font-bold tracking-wider text-muted-foreground uppercase">
        {label}
      </span>
      <span className="mt-1 text-2xl font-bold text-foreground tabular-nums">{value}</span>
      {sub ? <span className="mt-0.5 text-[11px] text-muted-foreground">{sub}</span> : null}
    </div>
  );
}

function StepArrow({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center px-1 text-center">
      <span className="text-[10px] font-medium text-muted-foreground">{label}</span>
      <span className="text-slate-300">&rarr;</span>
    </div>
  );
}

function Funnel({ report }: { report: CampaignReport }) {
  // Clicks come only from the Pull API (24-48h lag) while installs arrive via
  // real-time postback, so installs can exceed clicks transiently. Never render a
  // negative drop-off - flag the lag instead.
  const clicksLag = report.clicks > 0 && report.installs > report.clicks;
  const installLabel = clicksLag ? 'clicks syncing' : fmtPct(rate(report.installs, report.clicks));
  const convLabel = fmtPct(rate(report.conversions, report.installs));
  return (
    <div className="flex items-stretch gap-1.5">
      <FunnelStage label="Clicks" value={fmtInt(report.clicks)} sub="tracked link taps" />
      <StepArrow label={installLabel} />
      <FunnelStage label="Installs" value={fmtInt(report.installs)} />
      <StepArrow label={convLabel} />
      <FunnelStage
        label="Conversions"
        value={fmtInt(report.conversions)}
        sub={`${fmtMoney(report.revenue)} revenue`}
        accent
      />
    </div>
  );
}

function FreshnessBar({ health }: { health?: CampaignHealth }) {
  const live = health?.last_aggregated_at ?? health?.last_postback_at ?? null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-card/80 px-3 py-2 text-caption-regular text-muted-foreground">
      <Pulse className="size-3.5 text-emerald-500" />
      <span className="font-medium">
        {live ? `Live · updated ${fmtRelative(live)}` : 'Connected · waiting for activity'}
      </span>
      {health?.last_pull_sync_at ? (
        <span className="ml-auto text-muted-foreground">
          Last Pull sync {fmtRelative(health.last_pull_sync_at)}
        </span>
      ) : null}
    </div>
  );
}

function VelocityChart({ series }: { series: SeriesPoint[] }) {
  if (series.length === 0) {
    return (
      <div className="flex h-[220px] items-center justify-center rounded-xl border border-border bg-card text-body-sm-regular text-muted-foreground">
        Revenue appears here as conversions land.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
          <defs>
            <linearGradient id="afRevGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLORS.C1} stopOpacity={0.3} />
              <stop offset="100%" stopColor={CHART_COLORS.C1} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
          <XAxis
            dataKey="date"
            tickFormatter={fmtDateShort}
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tickFormatter={(v) => fmtAxisMoney(v as number)}
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
            width={64}
          />
          <Tooltip
            formatter={(value) => [fmtMoney(Number(value) || 0), 'Revenue']}
            labelFormatter={(l) => fmtDateShort(String(l))}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
          />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke={CHART_COLORS.C1}
            strokeWidth={2}
            fill="url(#afRevGrad)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function TrustNote() {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-card px-3 py-2 text-[11px] text-muted-foreground">
      <Info className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <span>
        Figures reflect events AppsFlyer attributes to your tracked links. Accuracy depends on your
        AppsFlyer Sending Option being set to &ldquo;Only events attributed to this partner&rdquo; -
        Discovr cannot verify that setting via API.
      </span>
    </div>
  );
}

export function ConversionsTab({ campaignId, onConnectionChange }: ConversionsTabProps) {
  const id = campaignId ?? '';
  const reportQ = useCampaignReport(id);
  const seriesQ = useCampaignSeries(id);
  const healthQ = useCampaignHealth(id);

  const report = reportQ.data;
  const series = seriesQ.data ?? [];
  const health = healthQ.data;

  const notConfigured =
    reportQ.error instanceof ApiError &&
    (reportQ.error.httpStatus === 400 || reportQ.error.httpStatus === 404);

  useEffect(() => {
    if (!reportQ.isLoading) onConnectionChange?.(!notConfigured);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportQ.isLoading, notConfigured]);

  if (reportQ.isLoading) {
    return (
      <div className="py-16 text-center text-body-sm-regular text-muted-foreground">
        Loading attribution…
      </div>
    );
  }

  if (notConfigured) {
    // A parent stacking multiple attribution sources renders its own shared
    // empty state via onConnectionChange instead of this one.
    if (onConnectionChange) return null;
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border py-16 text-center">
        <TrendUp className="size-6 text-indigo-400" />
        <h3 className="text-body-md-semibold text-foreground">
          See the real impact of your campaign
        </h3>
        <p className="max-w-sm text-body-sm-regular text-muted-foreground">
          Connect AppsFlyer to track installs, conversions and revenue driven by each creator.
        </p>
      </div>
    );
  }

  const rows: CreatorRow[] = (report?.creators ?? []).map((c) => ({ ...c, id: c.creator_id }));
  const cpa = report && report.conversions > 0 ? report.spend / report.conversions : null;

  return (
    <div className="flex w-full flex-col gap-4">
      <FreshnessBar health={health} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiStatCard
          label="Tracked ROAS"
          value={fmtRoas(report?.roas ?? null)}
          subtext={roasBenchmarkSubtext(report?.roas)}
          icon={<TrendUp />}
          accentColor={CHART_COLORS.C5}
        />
        <KpiStatCard
          label="Attributed Revenue"
          value={fmtMoney(report?.revenue ?? 0)}
          subtext={`${fmtInt(report?.conversions ?? 0)} conversions`}
          icon={<Receipt />}
          accentColor={CHART_COLORS.C4}
        />
        <KpiStatCard
          label="Conversions"
          value={fmtInt(report?.conversions ?? 0)}
          subtext="primary in-app event"
          icon={<Target />}
          accentColor={CHART_COLORS.C2}
        />
        <KpiStatCard
          label="CPA"
          value={cpa == null ? 'N/A' : fmtMoney(cpa)}
          subtext="cost per conversion (creator fee)"
          icon={<DownloadSimple />}
          accentColor={CHART_COLORS.C3}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-4">
          <span className="text-caption-medium text-foreground">Conversion funnel</span>
          {report ? <Funnel report={report} /> : null}
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-caption-medium text-foreground">Revenue velocity</span>
          <VelocityChart series={series} />
        </div>
      </div>

      <DataTable<CreatorRow>
        columns={creatorColumns}
        data={rows}
        getRowId={(r) => r.id}
        loading={reportQ.isLoading}
        error={(reportQ.error as ApiError | null) ?? null}
        onRetry={() => void reportQ.refetch()}
        emptyState="No attribution data yet - data appears once creators share their tracked links."
      />

      <TrustNote />
    </div>
  );
}
